import { z } from 'zod'
import { normalizedHexColorSchema } from '../validators/tenant.validator.js'
import { jsonValueSchema } from '../helpers/json.js'
import { activityActorKindSchema } from '../enums/actor-kind.enum.js'
import { tenantActivityTypeSchema } from '../enums/tenant-activity-type.enum.js'
import { tenantRoleSchema } from '../enums/tenant-role.enum.js'
import { tenantStatusSchema } from '../enums/tenant-status.enum.js'
import { uuidSchema } from '../validators/params.js'
import { cornerRadiusSchema } from '../enums/corner-radius.enum.js'
import { fontFamilySchema } from '../enums/font-family.enum.js'

export const teamMemberDtoSchema = z.object({
  userId: uuidSchema,
  name: z.string(),
  email: z.string().email(),
  role: tenantRoleSchema,
  joinedAt: z.string().datetime(),
})

export type TeamMemberDto = z.infer<typeof teamMemberDtoSchema>

export const teamInvitationDtoSchema = z.object({
  id: uuidSchema,
  email: z.string().email(),
  role: tenantRoleSchema,
  expiresAt: z.string().datetime(),
  invitedBy: z.string().nullable(),
  createdAt: z.string().datetime(),
})

export type TeamInvitationDto = z.infer<typeof teamInvitationDtoSchema>

export const teamDtoSchema = z.object({
  members: z.array(teamMemberDtoSchema),
  invitations: z.array(teamInvitationDtoSchema),
})

export type TeamDto = z.infer<typeof teamDtoSchema>

/** A tenant as the staff ops console lists it. */
export const opsTenantListItemDtoSchema = z.object({
  id: uuidSchema,
  slug: z.string(),
  name: z.string(),
  displayName: z.string(),
  status: tenantStatusSchema,
  memberCount: z.number().int().nonnegative(),
  ownerCount: z.number().int().nonnegative(),
  pendingInvitationCount: z.number().int().nonnegative(),
  /** Active (unrevoked) API keys per mode. */
  keyCounts: z.object({ live: z.number().int().nonnegative(), test: z.number().int().nonnegative() }),
  /** When any of the tenant's keys last authenticated (to the minute), or null if never. */
  lastApiUseAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
})

export type OpsTenantListItemDto = z.infer<typeof opsTenantListItemDtoSchema>

export const opsTenantListDtoSchema = z.object({
  tenants: z.array(opsTenantListItemDtoSchema),
})

export type OpsTenantListDto = z.infer<typeof opsTenantListDtoSchema>

export const opsTenantDetailDtoSchema = opsTenantListItemDtoSchema.extend({
  fromName: z.string(),
  // Not .email(): stored values predate validation (e.g. "noreply@localhost") and must still load.
  replyTo: z.string(),
  logoUrl: z.string().nullable(),
  /** Our stored copy of the logo, as a path on the API (load it from there; never from logoUrl). Null when there's none. */
  logoSrc: z.string().nullable(),
  accentColor: normalizedHexColorSchema.nullable(),
  cornerRadius: cornerRadiusSchema,
  fontFamily: fontFamilySchema,
  timezone: z.string(),
  /** What the organization charges a signer for a paper copy; 0 is free. */
  paperCopyFeeMicros: z.number().int(),
  suspendedAt: z.string().datetime().nullable(),
  suspendedReason: z.string().nullable(),
  closedAt: z.string().datetime().nullable(),
  closedReason: z.string().nullable(),
  /** While closed and not yet purged: when the envelope files will be deleted. */
  purgeAfter: z.string().datetime().nullable(),
  /** When a closed organization's files were purged; it can't be reopened after that. */
  purgedAt: z.string().datetime().nullable(),
  team: teamDtoSchema,
})

export type OpsTenantDetailDto = z.infer<typeof opsTenantDetailDtoSchema>

/** One entry in a tenant's activity log (team, profile, and status changes). */
export const tenantActivityDtoSchema = z.object({
  id: uuidSchema,
  type: tenantActivityTypeSchema,
  actor: z.object({
    kind: activityActorKindSchema,
    /** The person's name, "PixelSign staff" for staff, or null for system. */
    name: z.string().nullable(),
  }),
  /** Type-specific details (email and role for member events, changed fields, reason). */
  payload: z.record(z.string(), jsonValueSchema),
  ip: z.string().nullable(),
  createdAt: z.string().datetime(),
})

export type TenantActivityDto = z.infer<typeof tenantActivityDtoSchema>

export const tenantActivityListDtoSchema = z.object({
  events: z.array(tenantActivityDtoSchema),
})

export type TenantActivityListDto = z.infer<typeof tenantActivityListDtoSchema>
